import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';

import type { Id, IztContentNode } from '../../api/types';
import { EmptyState, Spinner, Toast } from '../../components';
import { contentFilterMatches, normalizeContentType, type ContentFilter } from '../../config/content-types';
import { featureEnabled } from '../../config/features';
import { AlertTriangleIcon, FolderIcon, SearchIcon } from '../../layout/icons';
import { readRecentLectures } from '../recent';
import { useDynamicDocumentStore } from '../filmstrip/documents';
import { buildAiTestCourse } from '../quiz/aiTestCourse';
import { useQltIztContentStore, useQltAuthStore } from '../../stores/qlt';
import { CourseDetail, CourseStrip, SearchHitList, UnitList, flattenUnits, type LectureLibrarySelection } from './LectureLibraryTree';
import { TypeChips } from './TypeChips';
import { msg, useLanguage, useMsg, type Msg } from '../../i18n/msg';

// the period's suggestion is its own rail item (Đề xuất), so the library keeps the browse tabs only
type LibraryTab = 'related' | 'recent';

const TABS: Array<{ id: LibraryTab; labelKey: 'library.tab.related' | 'library.tab.recent' }> = [
  { id: 'related', labelKey: 'library.tab.related' },
  { id: 'recent', labelKey: 'library.tab.recent' },
];

const TAB_EMPTY_KEY: Record<LibraryTab, 'library.empty.related' | 'library.empty.recent'> = {
  related: 'library.empty.related',
  recent: 'library.empty.recent',
};

export interface LectureLibraryPanelProps {
  classId: Id;
  gradeId?: Id;
  subjectId?: Id;
  /** Unit currently loaded on the canvas - drives the selected row. */
  activeUnitId?: string | null;
  /** Course detail state lives in the host, which swaps the panel header for it. */
  openCourse: IztContentNode | null;
  onOpenCourse: (course: IztContentNode) => void;
  onRequestLecture: (selection: LectureLibrarySelection) => void;
  /** Drag is additive to the confirm modal - the host owns the canvas drop ring. */
  onDragLecture?: (selection: LectureLibrarySelection | null) => void;
  initialFilter?: ContentFilter;
}

/** Recents store only an id and a name, so resolve them against the tree rather than guess a type. */
function resolveRecent(tree: IztContentNode[], filter: ContentFilter): LectureLibrarySelection[] {
  const wanted = readRecentLectures({ teacherId: useQltAuthStore.getState().teacherId }).slice(0, 6);
  const rows: LectureLibrarySelection[] = [];
  wanted.forEach((item) => {
    tree.forEach((course) => course.sections.forEach((section) => section.units.forEach((unit) => {
      if (unit.id !== item.unitId) return;
      if (!contentFilterMatches(filter, unit.type)) return;
      if (rows.filter((row) => row.unit.id === unit.id).length === 0) rows.push({ unit, courseId: course.id });
    })));
  });
  return rows;
}

export function LectureLibraryPanel({ classId, gradeId, subjectId, activeUnitId, openCourse, onOpenCourse, onRequestLecture, onDragLecture, initialFilter }: LectureLibraryPanelProps) {
  const { t } = useTranslation(['panels', 'common']);
  const tr = useMsg();
  const language = useLanguage();
  const rawTree = useQltIztContentStore((state) => state.tree);
  const loading = useQltIztContentStore((state) => state.loading);
  const error = useQltIztContentStore((state) => state.error);
  const loadTree = useQltIztContentStore((state) => state.loadTree);
  // one source: the generated quizzes the filmstrip shows, projected as a Kho bài course
  const generated = useDynamicDocumentStore((state) => state.items);
  const aiCourse = useMemo(() => buildAiTestCourse(generated), [generated, language]);
  const tree = useMemo(() => (aiCourse ? [aiCourse, ...rawTree] : rawTree), [aiCourse, rawTree]);
  const [filter, setFilter] = useState<ContentFilter>(initialFilter || 'all');
  const [tab, setTab] = useState<LibraryTab>('related');
  const [query, setQuery] = useState('');
  const [notice, setNotice] = useState<Msg | null>(null);

  useEffect(() => {
    if (initialFilter) setFilter(initialFilter);
  }, [initialFilter]);

  const searchable = featureEnabled('lectureLibrary.search');
  const normalizedQuery = searchable ? query.trim().toLocaleLowerCase('vi-VN') : '';
  const hits = useMemo(() => flattenUnits(tree, filter, normalizedQuery), [tree, filter, normalizedQuery]);
  const related = useMemo(() => flattenUnits(tree, filter, ''), [tree, filter]);
  const recent = useMemo(() => resolveRecent(tree, filter), [tree, filter]);
  const courses = useMemo(() => (normalizedQuery ? tree.filter((course) => hits.filter((row) => row.courseId === course.id).length > 0) : []), [normalizedQuery, hits, tree]);

  useEffect(() => {
    void loadTree({ class: classId, grade: gradeId, subject: subjectId });
  }, [classId, gradeId, loadTree, subjectId]);

  function courseName(courseId?: string): string {
    const match = tree.filter((course) => course.id === courseId)[0];
    return match ? match.name : '';
  }

  function select(selection: LectureLibrarySelection) {
    // a unit with no asset opens on an honest empty state, so the only dead end held back here is
    // a genuine PDF with the pdf feature off
    const type = normalizeContentType(selection.unit.type);
    if (type === 'pdf' && !featureEnabled('board.pdf')) {
      setNotice(msg('panels:library.pdfDisabled'));
      return;
    }
    onRequestLecture(selection);
  }

  const toast = <Toast open={Boolean(notice)} kind="error" message={notice ?? ''} onClose={() => setNotice(null)} />;

  // the host has already swapped the header for the course title, so this is the body only
  if (openCourse) {
    return <><CourseDetail course={openCourse} activeUnitId={activeUnitId} onSelect={select} onDragChange={onDragLecture} /><div className="lib-spacer" />{toast}</>;
  }

  const tabRows = tab === 'recent' ? recent : related;

  return (
    <>
      <div className="lecture-library">
        {searchable ? (
          <div className="lib-search">
            <span className="lib-search__icon" aria-hidden={true}><SearchIcon size={16} /></span>
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder={t('library.searchPlaceholder')} aria-label={t('library.searchLabel')} />
          </div>
        ) : null}

        <TypeChips value={filter} onChange={setFilter} />

        {loading ? <Spinner label={t('library.loading')} size="small" /> : null}
        {error ? <EmptyState icon={<AlertTriangleIcon size={26} />} title={t('library.loadError')} description={tr(error)} action={<button type="button" className="secondary-button" onClick={() => void loadTree({ class: classId, grade: gradeId, subject: subjectId })}>{t('common:actions.retry')}</button>} /> : null}
        {!loading && !error && tree.length === 0 ? <EmptyState icon={<FolderIcon size={26} />} title={t('library.noLectures')} description={t('library.noLecturesHint')} /> : null}

        {!loading && !error && tree.length > 0 && normalizedQuery ? (
          <>
            <h3 className="lib-heading">{t('library.searchResults')}</h3>
            {hits.length > 0
              ? <SearchHitList rows={hits} courseName={courseName} activeUnitId={activeUnitId} onSelect={select} onDragChange={onDragLecture} />
              : <p className="lib-empty">{t('library.noSearchResults')}</p>}
            {courses.length > 0 ? (
              <>
                <div className="lib-divider" />
                <h3 className="lib-heading">{t('library.relatedCourses')}</h3>
                <CourseStrip courses={courses} onOpen={onOpenCourse} />
              </>
            ) : null}
          </>
        ) : null}

        {!loading && !error && tree.length > 0 && !normalizedQuery ? (
          <>
            <div className="lib-tabs" role="tablist" aria-label={t('library.tabsLabel')}>
              {TABS.map((item) => (
                <button type="button" key={item.id} role="tab" aria-selected={tab === item.id} tabIndex={tab === item.id ? 0 : -1} className={tab === item.id ? 'is-active' : ''} onClick={() => setTab(item.id)}>{t(item.labelKey)}</button>
              ))}
            </div>
            {tabRows.length > 0
              ? <UnitList rows={tabRows} activeUnitId={activeUnitId} onSelect={select} onDragChange={onDragLecture} />
              : <p className="lib-empty">{t(TAB_EMPTY_KEY[tab])}</p>}
          </>
        ) : null}
      </div>
      {toast}
    </>
  );
}
